import {useEffect, useState} from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
// Eager side-effect import: detect Chrome-extension embed mode while the
// `?embed=extension` query is still on window.location (before BrowserRouter
// strips it) and fire the embed_ready handshake to the parent side panel.
import './lib/extensionEmbed';
// Side-effect import: restore reactstrap's PopperContent transition default so
// tooltips/popovers stop warning about a missing `transition.timeout`.
import './lib/reactstrapPopperDefaults';
import {QueryClientProvider} from '@tanstack/react-query';
import {createBrowserRouter} from 'react-router';
import {RouterProvider} from 'react-router/dom';
import './scss/style.scss';
import {MsalProvider} from '@azure/msal-react';
import {
    AuthenticationResult,
    EventMessage,
    EventType,
    PublicClientApplication
} from '@azure/msal-browser';
import mermaid from 'mermaid';
import useClientConfig from './lib/useClientConfig';
import {Container} from 'reactstrap';
import HelpfruitBot from './components/helpfruit/HelpfruitBot';
import {addGTMNoscript, initializeGTM} from './lib/gtm';
import isNullOrWhiteSpace, {
    isNotNullOrWhiteSpace
} from './lib/isNullOrWhiteSpace';
import useThemeVariables from './lib/useThemeVariables';
import {VersionProvider} from './context/VersionContext';
import {TeamsProvider, useTeamsContext} from './context/TeamsContext';
import {AuthProvider} from './context/AuthContext';
import {RouteTrackingProvider} from './context/RouteTrackingContext.tsx';
import {pdfjs} from 'react-pdf';
import {VoiceControlSurfaceProvider} from './voice/VoiceControlSurfaceContext';
import {DialogProvider} from './components/ui/DialogProvider';
import {ErrorBoundary, RouteErrorBoundary} from './components/ErrorBoundary';
import {queryClient} from './lib/queryClient';
import {stripCacheBustParamFromUrl} from './lib/chunkReloadGuard';

// The stale-chunk recovery screen reloads with a throwaway `_r` query parameter to bypass
// cached copies of the page. Drop it from the address bar before the router reads the URL.
stripCacheBustParamFromUrl();

// Configure PDF.js worker globally
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url
).toString();

declare global {
    interface Window {
        faqbot: {
            unmount: () => void;
            mount: () => void;
            close: () => void;
            open: () => void;
            triggerEngagementEvent: (eventName: string) => void;
            askQuestion: (question: string) => void;
            setContext: (context: {
                oid: string;
                email: string;
                name: string;
            }) => void;
        };
    }
}

mermaid.initialize({
    startOnLoad: true,
    theme: 'dark'
});

const root = ReactDOM.createRoot(document.getElementById('root')!);

// Everything that previously lived inside <BrowserRouter> becomes a single root
// element. RouteTrackingProvider/VoiceControlSurfaceProvider use routing context
// (useLocation/useNavigate) so they must stay inside the router-provided element.
const RootApp = () => (
    <RouteTrackingProvider>
        <VoiceControlSurfaceProvider>
            <App />
            <HelpfruitBot />
        </VoiceControlSurfaceProvider>
    </RouteTrackingProvider>
);

// Data router (createBrowserRouter) is required to unlock APIs like useBlocker that
// don't work under the legacy <BrowserRouter>. A single catch-all route mounts the
// existing app tree; all real <Routes>/<Route> definitions remain in App.tsx as
// descendant routes. Created once at module scope so it's stable across renders.
//
// errorElement makes RouteErrorBoundary the boundary for render/loader errors thrown
// inside the mounted route tree — react-router intercepts those before they can reach
// the class ErrorBoundary, so the route-level boundary is required to show the recovery
// screen for in-route crashes.
const router = createBrowserRouter([
    {path: '*', element: <RootApp />, errorElement: <RouteErrorBoundary />}
]);

const MsalConfigWrapper = () => {
    const {
        clientId,
        tenantId,
        googleTagManagerId,
        primaryColor,
        error,
        isLoading
    } = useClientConfig();
    const [msalInstance, setMsalInstance] =
        useState<PublicClientApplication | null>(null);

    const {isRunningInTeams} = useTeamsContext();

    useThemeVariables({primaryColor, isLoading, error});

    useEffect(() => {
        if (!isLoading && isNotNullOrWhiteSpace(googleTagManagerId)) {
            initializeGTM(googleTagManagerId);
            addGTMNoscript(googleTagManagerId);
        }
    }, [isLoading, googleTagManagerId]);

    useEffect(() => {
        if (isLoading) {
            return;
        }

        if (isNullOrWhiteSpace(clientId) || isNullOrWhiteSpace(tenantId)) {
            return;
        }

        const msalConfig = {
            auth: {
                clientId: clientId,
                authority: `https://login.microsoftonline.com/${tenantId}`,
                redirectUri: `${window.location.origin}`,
                postLogoutRedirectUri: window.location.origin,
                navigateToLoginRequestUrl: false
            },
            cache: {
                cacheLocation: 'sessionStorage'
            }
        };

        const msalInstance = new PublicClientApplication(msalConfig);

        msalInstance.initialize().then(() => {
            const accounts = msalInstance.getAllAccounts();
            if (accounts.length > 0) {
                msalInstance.setActiveAccount(accounts[0]);
            }

            msalInstance.enableAccountStorageEvents();

            const callbackId = msalInstance.addEventCallback(
                (event: EventMessage) => {
                    if (
                        event.eventType === EventType.LOGIN_SUCCESS &&
                        event.payload
                    ) {
                        const payload = event.payload as AuthenticationResult;
                        const account = payload.account;
                        msalInstance.setActiveAccount(account);

                        // Invalidate user cache on login to ensure fresh data
                        fetch('/api/auth/invalidate-cache', {
                            method: 'POST',
                            headers: {
                                Authorization: `Bearer ${payload.accessToken}`
                            }
                        }).catch(error => {
                            console.error(
                                'Failed to invalidate cache on login:',
                                error
                            );
                        });
                    }
                }
            );

            setMsalInstance(msalInstance);

            return () => {
                if (callbackId != null) {
                    msalInstance.removeEventCallback(callbackId);
                }
            };
        });
    }, [isLoading, clientId, tenantId, isRunningInTeams]);

    if (isLoading) {
        return <Container>Loading configuration...</Container>;
    }

    if (error) {
        return (
            <Container>Error loading configuration: {error.message}</Container>
        );
    }

    if (isNullOrWhiteSpace(clientId) || isNullOrWhiteSpace(tenantId)) {
        return <Container>No client configuration available</Container>;
    }

    if (!msalInstance) {
        return <Container>Initializing authentication...</Container>;
    }

    return (
        <MsalProvider instance={msalInstance}>
            <AuthProvider>
                {/* DialogProvider sits outside RouterProvider so the app-wide
                    confirm/alert modal is available to every route (and a future
                    navigation guard, which must call useDialogs from within a route). */}
                <DialogProvider>
                    <RouterProvider router={router} />
                </DialogProvider>
            </AuthProvider>
        </MsalProvider>
    );
};

const AppWrapper = () => {
    return (
        <QueryClientProvider client={queryClient}>
            <VersionProvider>
                <TeamsProvider>
                    <MsalConfigWrapper />
                </TeamsProvider>
            </VersionProvider>
        </QueryClientProvider>
    );
};

// ErrorBoundary is the OUTERMOST wrapper so the recovery screen never depends on the
// provider/router tree that may itself be the broken thing. It catches render errors in
// the provider tree above the router; in-route errors are handled by RouteErrorBoundary.
root.render(
    <ErrorBoundary>
        <AppWrapper />
    </ErrorBoundary>
);
